import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  enqueueDirectoryContactsImportAdmin,
  getDirectoryContactsImportStatusAdmin,
  type DirectoryImportJobRow,
} from "@/lib/directory-contacts-admin.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authed/admin/directory-contacts")({
  head: () => ({
    meta: [
      { title: "Admin: Directory contacts import: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminDirectoryContactsPage,
});

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function statusChipClasses(status: string): string {
  switch (status) {
    case "complete":
      return "text-emerald-700 bg-emerald-50 border-emerald-200";
    case "failed":
      return "text-red-700 bg-red-50 border-red-200";
    case "running":
      return "text-amber-700 bg-amber-50 border-amber-200";
    default:
      return "text-brand-muted bg-muted/40 border-brand-line";
  }
}

function AdminDirectoryContactsPage() {
  const qc = useQueryClient();
  const fetchStatus = useServerFn(getDirectoryContactsImportStatusAdmin);
  const enqueue = useServerFn(enqueueDirectoryContactsImportAdmin);

  const status = useQuery({
    queryKey: ["admin-directory-contacts-status"],
    queryFn: () => fetchStatus(),
    refetchInterval: (query) => {
      const rows = query.state.data?.jobs ?? [];
      const active = rows.some(
        (j: DirectoryImportJobRow) => j.status === "pending" || j.status === "running",
      );
      return active ? 5_000 : false;
    },
  });

  const enqueueMut = useMutation({
    mutationFn: () => enqueue(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-directory-contacts-status"] }),
  });

  const jobs = status.data?.jobs ?? [];
  const hasActive = jobs.some((j) => j.status === "pending" || j.status === "running");

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-[28px] text-brand-ink">
            Directory contacts import
          </h1>
          <p className="mt-1 text-[13px] text-brand-muted">
            Reloads the directory-wide contacts table from the source workbook.
            The import job is picked up by the pipeline service and swaps into
            the live table atomically on success.
          </p>
        </div>
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted underline underline-offset-4"
        >
          ← Admin home
        </Link>
      </div>

      <section className="mb-6 grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-brand-line bg-brand-paper p-5">
          <div className="text-[11.5px] uppercase tracking-wide text-brand-muted">
            Contacts in live table
          </div>
          <div className="mt-2 font-serif text-[26px] text-brand-ink">
            {status.data ? status.data.liveRowCount.toLocaleString() : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-brand-line bg-brand-paper p-5">
          <div className="text-[11.5px] uppercase tracking-wide text-brand-muted">
            Contacts last imported
          </div>
          <div className="mt-2 font-serif text-[16px] text-brand-ink">
            {formatWhen(status.data?.lastImportedAt ?? null)}
          </div>
        </div>
      </section>

      <section className="mb-6 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-serif text-[18px] text-brand-ink">
              Start a new import
            </h2>
            <p className="mt-1 text-[12.5px] text-brand-muted">
              Queues a job for the pipeline service. Coexists with in-progress
              deals; nothing is blocked. Completes in a single atomic swap.
            </p>
          </div>
          <Button
            onClick={() => enqueueMut.mutate()}
            disabled={enqueueMut.isPending || hasActive}
          >
            {hasActive ? "Import in progress…" : "Import contacts"}
          </Button>
        </div>
        {enqueueMut.isError ? (
          <div className="mt-3 text-[12.5px] text-red-700">
            {enqueueMut.error instanceof Error
              ? enqueueMut.error.message
              : "Could not queue the import."}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-3 font-serif text-[18px] text-brand-ink">
          Recent imports
        </h2>
        {status.isLoading ? (
          <div className="text-[12.5px] text-brand-muted">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="text-[12.5px] text-brand-muted">
            No imports yet. Use the button above to queue the first one.
          </div>
        ) : (
          <ul className="space-y-3">
            {jobs.map((j) => (
              <li
                key={j.id}
                className="rounded-xl border border-brand-line bg-white/40 p-4"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-block rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${statusChipClasses(j.status)}`}
                  >
                    {j.status}
                  </span>
                  <span className="text-[11.5px] text-brand-muted">
                    Requested {formatWhen(j.requested_at)}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-3 text-[12.5px]">
                  <div>
                    <div className="text-brand-muted">Parsed</div>
                    <div className="font-mono text-brand-ink">
                      {j.parsed_count?.toLocaleString() ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-brand-muted">Placeholder excluded</div>
                    <div className="font-mono text-brand-ink">
                      {j.placeholder_excluded_count?.toLocaleString() ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-brand-muted">Inserted</div>
                    <div className="font-mono text-brand-ink">
                      {j.inserted_count?.toLocaleString() ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-brand-muted">Finished</div>
                    <div className="font-mono text-brand-ink">
                      {formatWhen(j.completed_at)}
                    </div>
                  </div>
                </div>
                {j.error_message ? (
                  <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-red-50 p-3 text-[11.5px] text-red-800">
                    {j.error_message}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}